'use client';

import { useMemo, useRef } from 'react';
import type { GameTrackingInfo, GameType } from '@/types/games';
import type { GameViewModel } from '@/types/games/game-view-model';
import { COLLECTION_ZONE_LAYOUT, PRISMIC_ZONE_LAYOUT } from '@/app/constants/zone';
// eslint-disable-next-line nextjs-boundaries/no-server-import-in-client
import { ZoneHeader } from '@/app/components/zones/subcomponents/zone-header/zone-header';
import { DraggableContainer } from '@/app/elements/draggable-wrapper/draggable-container';
import { ZoneLayoutProvider } from '@/app/components/zones/contexts/zone-layout-context';
import { GameFactoryClient } from '@/app/components/game/game-factory.client';
import { classes } from '@/utils/classes/classes';
import { useGlobalContext } from '@/store/contexts/global-context';
import { useGameZoneImpression } from '@/hooks/use-game-zone-impression/use-game-zone-impression';

export type GamesGridZoneMeta = {
    title?: string;
    categoryUid?: string;
    zoneType: string;
    zoneLayout?: string;
    bannerId: string;
    zoneName: string;
    gridClassModifier?: string;
    isDynamicOrdering?: boolean;
};

type GamesGridZoneProps = {
    games: GameViewModel[];
    tracking: GameTrackingInfo[];
    gridLayout: PRISMIC_ZONE_LAYOUT;
    meta: GamesGridZoneMeta;
    isOnlyChildZone?: boolean;
    itemsType?: GameType;
    hideGameInfo?: boolean;
};

export const GamesGridZone = ({
    games,
    tracking,
    gridLayout,
    meta,
    isOnlyChildZone = false,
    itemsType,
    hideGameInfo = false
}: GamesGridZoneProps) => {
    const { isMobile } = useGlobalContext();
    const containerRef = useRef<HTMLDivElement>(null);
    const appliedZoneLayout = COLLECTION_ZONE_LAYOUT[gridLayout];

    const gameElements = useMemo(
        () =>
            games.map((game, index) => (
                <GameFactoryClient
                    key={game.uid}
                    gameType={itemsType}
                    game={game}
                    index={index + 1}
                    layout={gridLayout}
                    hideGameInfo={hideGameInfo}
                    priority={index === 0}
                    tracking={{ gamePosition: index }}
                />
            )),
        [games, itemsType, gridLayout, hideGameInfo]
    );

    useGameZoneImpression({
        containerRef,
        items: tracking,
        zoneConfig: {
            zoneName: meta.zoneName,
            zoneType: meta.zoneType,
            zoneLayout: meta.zoneLayout ?? appliedZoneLayout ?? null
        },
        bannerId: meta.bannerId,
        bannerName: meta.zoneName
    });

    return (
        <ZoneLayoutProvider layout={gridLayout}>
            {!isOnlyChildZone && <ZoneHeader title={meta.title} categoryUid={meta.categoryUid} />}
            <div ref={containerRef} data-t-bannerid={meta.bannerId} data-t-zonetype={meta.zoneType}>
                <DraggableContainer indicators={!isMobile}>
                    <div
                        className={classes('grid-zone-layout', meta.gridClassModifier, appliedZoneLayout)}
                        data-t-zonename={meta.zoneName}
                        data-t-zonemodule={meta.bannerId}
                        data-t-zonelayout={meta.zoneLayout ?? appliedZoneLayout}
                        data-t-dynamicordering={meta.isDynamicOrdering ? 'true' : 'false'}>
                        {gameElements}
                    </div>
                </DraggableContainer>
            </div>
        </ZoneLayoutProvider>
    );
};
